import React, { useState, useEffect } from "react";
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
    CircularProgress,
} from "@mui/material";
import HotelService from "../../api/hotelService";
import { Hotel } from "../../types/hotel.d";

interface IAddHotel {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    onHotelAdded: (hotel: Hotel) => void;
}

const AddHotel: React.FC<IAddHotel> = ({ isOpen, setIsOpen, onHotelAdded }) => {
    const [hotelName, setHotelName] = useState<string>("");
    const [rooms, setRooms] = useState<number | string>("");
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (!isOpen) {
            setHotelName("");
            setRooms("");
            setError(null);
            setLoading(false);
        }
    }, [isOpen]);

    const handleSubmit = async () => {
        if (hotelName.trim() && Number(rooms) > 0) {
            setLoading(true);
            try {
                setError(null);

                const newHotel = await HotelService.createHotel({
                    name: hotelName,
                    maxUnitsCapacity: Number(rooms),
                });

                onHotelAdded(newHotel);
                setIsOpen(false);
            } catch (err: any) {
                const apiError = err.response?.data?.message || "Une erreur est survenue lors de l'ajout de l'hôtel.";
                setError(apiError);
            } finally {
                setLoading(false);
            }
        } else {
            setError("Veuillez remplir tous les champs correctement.");
        }
    };

    const isSubmitDisabled = loading || !hotelName.trim() || Number(rooms) <= 0;

    return (
        <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
            <DialogTitle align="center">Ajout d'un hôtel</DialogTitle>
            <DialogContent>
                <Stack spacing={2} mt={1}>
                    {error && <Typography color="error">{error}</Typography>}
                    <TextField
                        label="Nom de l'hôtel"
                        fullWidth
                        value={hotelName}
                        onChange={(e) => setHotelName(e.target.value)}
                        required
                    />
                    <TextField
                        label="Chambres"
                        type="number"
                        value={rooms}
                        onChange={(e) => {
                            const value = Number(e.target.value);
                            if (value < 0) {
                                return;
                            }
                            setRooms(e.target.value);
                        }}
                        required
                        inputProps={{ min: 1 }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isSubmitDisabled}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {loading ? "Ajout en cours..." : "Confirmer"}
                    </Button>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default AddHotel;
